import { Filter as FilterIcon, Plus } from 'lucide-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import NewClassificationTree from './classification/newClassification';
import Filter from './filter/Filter';
import Bar from './elements/Bar';
import { Input } from './ui/Input';
import { fetchPickerPages } from './modals/fetchPickerPages';

export default function PanelWithClassification({
  makeActive,
  active,
  addItem,
  title = null,
  filteredValues = {
    is_active: [
      { value: 0, title: 'Aktivní' },
      { value: 1, title: 'Neaktivní' },
    ],
  } as any,
  onSearch,
  valueAsObject = false,
  url,
  disableAdd = false,
  // tempraly solution, remove later when backend will be ready
  disabledOptionsFlag = false,
  width = '420px',
  className = '',
  selectedCounts = undefined as Record<string, number> | undefined,
}) {
  const permission = useSelector((state) => state.auth.pagePermission);
  const [classificationItems, setClassificationItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues] = useState(null);

  const _url = `/api/v1/classification/${url}`;
  const PAGE_SIZE = 30;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const childRequests = useRef(new Map<string, AbortController>());

  useEffect(() => {
    const controller = new AbortController();
    generation.current += 1;
    childRequests.current.forEach((request) => request.abort());
    childRequests.current.clear();
    setLoading(true);
    setError('');
    const timeout = setTimeout(
      () => {
        fetchPickerPages<any>(
          _url,
          { search: searchTerm || undefined, page_size: PAGE_SIZE, ...filterValues },
          controller.signal
        )
          .then((data) => {
            if (!controller.signal.aborted) setClassificationItems(data);
          })
          .catch(() => {
            if (!controller.signal.aborted) setError('Skupiny se nepodařilo načíst.');
          })
          .finally(() => {
            if (!controller.signal.aborted) setLoading(false);
          });
      },
      searchTerm ? 300 : 0
    );
    return () => {
      clearTimeout(timeout);
      controller.abort();
      childRequests.current.forEach((request) => request.abort());
      childRequests.current.clear();
    };
  }, [_url, searchTerm, retry]);

  const getClassificationChildren = async (parent, requestUrl) => {
    const key = String(parent);
    if (childRequests.current.has(key)) return;
    const controller = new AbortController();
    const currentGeneration = generation.current;
    childRequests.current.set(key, controller);
    try {
      const children = await fetchPickerPages<any>(
        requestUrl,
        { parent, page_size: PAGE_SIZE },
        controller.signal
      );
      if (controller.signal.aborted || currentGeneration !== generation.current) return;
      const append = (nodes) =>
        nodes.map((node) =>
          node.id === parent
            ? {
                ...node,
                children: [
                  ...new Map(
                    [...(node.children || []), ...children].map((child) => [child.id, child])
                  ).values(),
                ],
              }
            : { ...node, children: append(node.children || []) }
        );
      setClassificationItems((nodes) => append(nodes));
    } catch {
      if (!controller.signal.aborted)
        setError('Podskupiny se nepodařilo načíst. Zkuste skupinu rozbalit znovu.');
    } finally {
      if (childRequests.current.get(key) === controller) childRequests.current.delete(key);
    }
  };

  const itemTree = (item) => {
    if (!item) return null;
    const commonProps = {
      content: item.title,
      active: item.id === active,
      reference: item.reference,
      referenceDirect: item.reference_direct,
      selectedCount: selectedCounts?.[String(item.id)],
      pickerMode: selectedCounts !== undefined,
      is_disabled: item.is_disabled,
      disabledOptionsFlag: disabledOptionsFlag,
      onClick: () => {
        if (valueAsObject) {
          makeActive(item);
        } else {
          makeActive(item.id);
        }
      },
      visible: true,
      has_children: item.has_children,
    };

    if ((item.children || []).length === 0) {
      return (
        <NewClassificationTree
          key={item.id}
          {...commonProps}
          onExpand={() => {
            getClassificationChildren(item.id, _url);
          }}
        />
      );
    }
    return (
      <NewClassificationTree
        key={item.id}
        {...commonProps}
        status={item.status}
        onExpand={() => {}}
        canHide
      >
        {item.children.map((child) => itemTree(child))}
      </NewClassificationTree>
    );
  };

  const makeTree = (data) => (data || []).map((ele) => itemTree(ele));

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const content = () => {
    const items = [
      <Filter key={1} filteredValues={filteredValues}>
        <FilterIcon className="h-4 w-4" data-testid="panel-classification-filter-btn" />
      </Filter>,
    ];
    if (permission === 2 && !disableAdd) {
      if (active) {
        items.push(
          <Plus
            key={2}
            className="h-4 w-4"
            data-testid="panel-classification-add-btn"
            onClick={addItem}
          />
        );
      }
    }
    return items;
  };

  return (
    <div className="border-r border-neutral-150 flex flex-col shrink-0 h-full" style={{ width }}>
      {title && <Bar text={title} content={[]} />}
      <div className="p-3 border-b border-neutral-150 bg-neutral-25/50">
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <Input
            placeholder="Vyhledat..."
            data-testid="panel-classification-search-input"
            aria-label="Vyhledat skupiny"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              onSearch?.();
            }}
            className="h-8 pl-8 text-xs"
          />
        </div>
      </div>
      <div className={classNames('overflow-y-auto flex-1 py-1', className)}>
        {error && (
          <div role="alert" className="p-3 text-xs">
            {error}{' '}
            <button
              type="button"
              className="underline"
              onClick={() => setRetry((value) => value + 1)}
            >
              Zkusit znovu
            </button>
          </div>
        )}
        {loading ? (
          <p role="status" className="p-3 text-xs">
            Načítám skupiny…
          </p>
        ) : (
          makeTree(classificationItems)
        )}
      </div>
    </div>
  );
}

PanelWithClassification.propTypes = {
  data: PropTypes.array,
  makeActive: PropTypes.func,
  active: PropTypes.string,
  addItem: PropTypes.func,
  onFilter: PropTypes.func,
  title: PropTypes.string,
  onSearch: PropTypes.func,
  filteredValues: PropTypes.object,
  disableAdd: PropTypes.bool,
};
